import { useEffect, useRef, useCallback } from "react";
import { Routes, useLocation } from "react-router-dom";
import Navbar from "../../components/landing/Navbar";
import Footer from "../../components/landing/Footer";
import { resolveDocNavWithIcons } from "./config/docNavIcons";
import { DOC_NAV } from "./config/docNav.config";
import { renderDocsSectionRoutes } from "./config/docsRoutes.config";
import { hasSectionConfig } from "./config/sections";
import { isDocsLocalMode } from "./constants/docsEnv";
import { validateDocConfig } from "../../utils/docUtils/docsNavigationSelectors";
import { validateDocRoutes, getSectionIdFromPathname } from "../../utils/docUtils/docsRouteUtils";
import { useDocsNavigation } from "../../hooks/useDocsNavigation";
import { useDocsMobileSidebar } from "../../hooks/useDocsMobileSidebar";
import { useDocsConfig } from "./context/DocsConfigContext";
import DocsLayout from "../../components/docs/DocsLayout";
import DocsContentSpinner from "../../components/docs/DocsContentSpinner";
import DocsUnavailable from "../../components/docs/DocsUnavailable";
import pageStyles from "../../styles/docs/docsPage.module.scss";

const fallbackDocNav = resolveDocNavWithIcons(DOC_NAV);

const DocsPageContent = () => {
  const location = useLocation();
  const contentRef = useRef(null);
  const { navStatus, docNav } = useDocsConfig();
  const resolvedDocNav = navStatus === "ready" ? docNav : fallbackDocNav;
  const activeId =
    getSectionIdFromPathname(resolvedDocNav, location.pathname) ?? "overview";

  const scrollToTop = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const { mobileSidebarOpen, close, toggle } = useDocsMobileSidebar();

  const closeMobileAndScroll = useCallback(() => {
    close();
    scrollToTop();
  }, [close, scrollToTop]);

  const { activeGroup, activeItem, prev, next } = useDocsNavigation(
    navStatus === "ready" ? docNav : [],
    activeId,
  );

  useEffect(() => {
    close();
    scrollToTop();
  }, [location.pathname, close, scrollToTop]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development" || !isDocsLocalMode()) {
      return;
    }

    const missingContent = validateDocConfig(fallbackDocNav, hasSectionConfig);
    if (missingContent.length > 0) {
      console.warn("[docs] Nav sections missing content:", missingContent);
    }

    const routeErrors = validateDocRoutes(fallbackDocNav);
    if (routeErrors.length > 0) {
      console.warn("[docs] Route config errors:", routeErrors);
    }
  }, []);

  const renderMainContent = () => {
    if (navStatus === "loading") {
      return (
        <main className={pageStyles.main}>
          <div className={pageStyles.contentWrapper}>
            <DocsContentSpinner />
          </div>
        </main>
      );
    }

    if (navStatus === "error") {
      return (
        <main className={pageStyles.main}>
          <div className={pageStyles.contentWrapper}>
            <DocsUnavailable />
          </div>
        </main>
      );
    }

    return (
      <DocsLayout
        ref={contentRef}
        docNav={docNav}
        activeId={activeId}
        onNavigate={closeMobileAndScroll}
        activeGroup={activeGroup}
        activeItem={activeItem}
        prev={prev}
        next={next}
        mobileSidebarOpen={mobileSidebarOpen}
        onMobileToggle={toggle}
        onMobileClose={close}
      >
        <Routes>{renderDocsSectionRoutes(docNav)}</Routes>
      </DocsLayout>
    );
  };

  return (
    <div className={pageStyles.page}>
      <Navbar />

      {navStatus === "ready" ? (
        renderMainContent()
      ) : (
        <div className={pageStyles.layout}>{renderMainContent()}</div>
      )}

      <Footer />
    </div>
  );
};

const DocsPage = () => <DocsPageContent />;

export default DocsPage;
